import { Auth } from 'aws-amplify'
import { useEffect, useState } from 'react'

import { REMEMBERME, SETTINGS, USER_TOKEN } from 'config'
import { type IAuthForm } from 'containers/Auth/SignIn/component'
import { APIs, type ICampaign } from 'services'
import { useLocalStorage } from './useLocalStorage'

export interface UseProvideAuthType {
  isLoading: boolean
  checkingUser: boolean
  userEmail: string
  rememberMe: boolean
  authToken: string
  settings: any
  account: any

  handleRememberMe: (rememberMe: boolean) => void
  handleLogin: ({ email, password }: IAuthForm) => Promise<any>
  handleLogOut: () => Promise<void>
  getUser: (accountId: string, email: string) => Promise<any>
  getUserAsAdmin: (email: string) => Promise<any>
  updateEmail: (
    email: string,
    newEmail: string,
    accountId: string
  ) => Promise<any>
  getICStatus: (email: string) => Promise<any>
  getICAccounts: (email: string) => Promise<any>
  registerICAccount: (params: any) => Promise<any>
  unregisterICAccount: (params: any) => Promise<any>
  changePasswordICAccount: (params: any) => Promise<any>
  linkICAccounts: (params: any) => Promise<any>
  unlinkICAccounts: (params: any) => Promise<any>

  getCCSAccount: (params: any) => Promise<any>
  manualConfirmUser: (params: any) => Promise<any>

  uploadAssets: (formData: FormData) => Promise<any>
  deleteAssets: (key: string) => Promise<any>
  listAssets: () => Promise<any>
  renameAssets: (oldName: string, newName: string) => Promise<any>

  getCampaigns: () => Promise<any>
  getCampaignById: (id: string) => Promise<any>
  createCampaign: (params: ICampaign) => Promise<any>
  editCampaign: (campaignId: string, params: ICampaign) => Promise<any>
  sendCampaign: (param: any) => Promise<any>
  deleteCampaign: (campaignId: string) => Promise<any>
  getCampaignLogsById: (
    campaignId: string,
    startDate: string,
    endDate: string
  ) => Promise<any>
  getSettings: (authToken: string) => Promise<any>
  updateMailingAddress: (param: any) => Promise<any>
  updateInfo: (param: any) => Promise<any>
  unregisterAccount: (params: any) => Promise<any>
  linkAccount: (params: any) => Promise<any>
  getAllLinkedAccounts: (params: any) => Promise<any>
  updateCognitoAttribute: (param: any) => Promise<any>
}

export const useProvideAuth = (): UseProvideAuthType => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userEmail, setUserEmail] = useState<string>('')
  const [checkingUser, setCheckingUser] = useState<boolean>(false)
  const [rememberMe, setRememberMe] = useLocalStorage(REMEMBERME, false)
  const [authToken, setAuthToken] = useLocalStorage(USER_TOKEN, '')
  const [settings, setSettings] = useLocalStorage<any>(SETTINGS, [])
  const [account, setAccount] = useState<any | null>(null)

  const handleLogin = async ({ email, password }: IAuthForm): Promise<any> => {
    setIsLoading(true)
    try {
      const user = await Auth.signIn(email, password)
      if (user?.challengeName === 'NEW_PASSWORD_REQUIRED') {
        return user
      } else {
        setUserEmail(user?.username)
        setAuthToken(user?.signInUserSession?.accessToken?.jwtToken)
        await getSettings(user?.signInUserSession?.accessToken?.jwtToken)
        setIsLoading(false)
        setCheckingUser(true)
        return user
      }
    } catch (error: any) {
      setIsLoading(false)
      throw error?.message ?? error?.response?.data?.message ?? 'Login failed'
    }
  }


  const handleLogOut = async (): Promise<void> => {
    setIsLoading(true)
    setCheckingUser(false)
    setAuthToken('')
    await Auth.signOut()
    setSettings([])
    setIsLoading(false)
  }

  const handleRememberMe = (value: boolean): void => {
    setRememberMe(value)
  }

  const getUser = async (accountId: string, email: string): Promise<any> => {
    try {
      const accountData = await APIs.getUser(
        {
          searchParams: {
            accountId,
            email,
            error: false
          }
        },
        authToken
      )
      setAccount(accountData)
      return accountData
    } catch (error: any) {
      setAccount({})
      throw error
    }
  }

  const getUserAsAdmin = async (email: string): Promise<any> => {
    return await APIs.getUserAsAdmin(
      {
        email
      },
      authToken
    )
  }

  const updateEmail = async (
    email: string,
    newEmail: string,
    accountId: string
  ): Promise<any> => {
    return await APIs.updateEmail(
      {
        accountId,
        email,
        newEmail,
        updates: [
          {
            object: 'PRIMARYEMAIL',
            value: newEmail
          }
        ]
      },
      authToken
    )
  }

  const getCampaigns = async (): Promise<any> => {
    return await APIs.getCampaigns(authToken)
  }

  const getCampaignById = async (id: string): Promise<any> => {
    return await APIs.getCampaignById(id, authToken)
  }

  const createCampaign = async (payload: ICampaign): Promise<any> => {
    return await APIs.createCampaign(payload, authToken)
  }

  const editCampaign = async (
    campaignId: string,
    payload: ICampaign
  ): Promise<any> => {
    return await APIs.editCampaign(campaignId, payload, authToken)
  }

  const sendCampaign = async (param: any): Promise<any> => {
    return await APIs.sendCampaign(param, authToken)
  }

  const deleteCampaign = async (campaignId: string): Promise<any> => {
    return await APIs.deleteCampaign(campaignId, authToken)
  }

  const getCampaignLogsById = async (
    campaignId: string,
    startDate: string,
    endDate: string
  ): Promise<any> => {
    return await APIs.getCampaignLogsById(
      campaignId,
      startDate,
      endDate,
      authToken
    )
  }

  const getSettings = async (authToken: string): Promise<any> => {
    try {
      const result = await APIs.getSettings(authToken)
      setSettings(result)
    } catch (error) {
      setSettings([])
    }
  }

  const uploadAssets = async (formData: FormData): Promise<any> => {
    return await APIs.uploadAssets(formData, authToken)
  }

  const deleteAssets = async (key: string): Promise<any> => {
    return await APIs.deleteAssets(key, authToken)
  }

  const listAssets = async (): Promise<any> => {
    return await APIs.listAssets(authToken)
  }

  const renameAssets = async (
    oldName: string,
    newName: string
  ): Promise<any> => {
    return await APIs.renameAssets(oldName, newName, authToken)
  }

  const getICStatus = async (email: string): Promise<any> => {
    return await APIs.getICStatus(email, authToken)
  }

  const getICAccounts = async (email: string): Promise<any> => {
    return await APIs.getICAccounts(email, authToken)
  }

  const registerICAccount = async (params: any): Promise<any> => {
    return await APIs.registerICAccount(params, authToken)
  }

  const unregisterICAccount = async (params: any): Promise<any> => {
    return await APIs.unregisterICAccount(params, authToken)
  }

  const changePasswordICAccount = async (params: any): Promise<any> => {
    return await APIs.changePasswordICAccount(params, authToken)
  }

  const linkICAccounts = async (params: any): Promise<any> => {
    return await APIs.linkICAccounts(params, authToken)
  }

  const unlinkICAccounts = async (params: any): Promise<any> => {
    return await APIs.unlinkICAccounts(params, authToken)
  }

  const getCCSAccount = async (params: any): Promise<any> => {
    return await APIs.getCCSAccount(params, authToken)
  }

  const manualConfirmUser = async (params: any): Promise<any> => {
    return await APIs.manualConfirmUser(params, authToken)
  }

  const updateMailingAddress = async (params: any): Promise<any> => {
    return await APIs.updateMailingAddress(params, authToken)
  }

  const updateInfo = async (params: any): Promise<any> => {
    return await APIs.updateInfo(params, authToken)
  }

  const unregisterAccount = async (params: any): Promise<any> => {
    return await APIs.unregisterAccount(params, authToken)
  }

  const linkAccount = async (params: any): Promise<any> => {
    return await APIs.linkAccount(params, authToken)
  }

  const getAllLinkedAccounts = async (params: any): Promise<any> => {
    return await APIs.getAllLinkedAccounts(params, authToken)
  }

  const updateCognitoAttribute = async (param: any): Promise<any> => {
    return await APIs.updateCognitoAttribute(param, authToken)
  }


  useEffect(() => {
    if (authToken.length > 0) {
      void getSettings(authToken)
    }
  }, [authToken])

  return {
    isLoading,
    userEmail,
    checkingUser,
    rememberMe,
    authToken,
    settings,
    account,
    linkAccount,
    handleLogin,
    handleLogOut,
    handleRememberMe,
    getUser,
    getUserAsAdmin,
    updateEmail,
    getCampaigns,
    getCampaignById,
    createCampaign,
    editCampaign,
    sendCampaign,
    deleteCampaign,
    getCampaignLogsById,
    getSettings,
    uploadAssets,
    deleteAssets,
    listAssets,
    renameAssets,
    getICStatus,
    getICAccounts,
    registerICAccount,
    unregisterICAccount,
    changePasswordICAccount,
    linkICAccounts,
    unlinkICAccounts,
    getCCSAccount,
    manualConfirmUser,
    updateMailingAddress,
    updateInfo,
    unregisterAccount,
    getAllLinkedAccounts,
    updateCognitoAttribute
  }
}
